import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { translateError } from '../../i18n.js';
import { api } from '../../state/auth.js';
import Button from './Button.jsx';
import Modal from './Modal.jsx';

const ERRORS = {
  confirm_name_mismatch: 'De naam komt niet overeen. Typ de naam precies zoals hij hierboven staat.',
  cannot_erase_self: 'Je kunt je eigen gegevens hier niet wissen. Vraag een andere beheerder.',
  platform_admin: 'Dit is een platformbeheerder. Die kan alleen door 4U worden verwijderd.',
};

// Erasing a member on request (AVG): their data in this club goes, history stays as "Verwijderd lid"
export default function EraseMemberModal({ user, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    setPreview(null); setTyped(''); setError(''); setResult(null);
    if (!user) return;
    // Why: a slow answer for someone opened earlier must not fill in this person's name
    let current = true;
    api(`/api/users/${user.id}/erase-preview`)
      .then(data => { if (current) setPreview(data); })
      .catch(err => { if (current) setError(ERRORS[err.code] || translateError(err.code)); });
    return () => { current = false; };
  }, [user?.id]);

  const matches = preview && typed.trim().toLowerCase() === String(preview.displayName || '').trim().toLowerCase();

  async function erase() {
    setBusy(true);
    setError('');
    try {
      setResult(await api(`/api/users/${user.id}/erase`, { method: 'POST', body: JSON.stringify({ confirm_name: typed }) }));
    } catch (err) {
      setError(ERRORS[err.code] || translateError(err.code));
    } finally {
      setBusy(false);
    }
  }

  function close() {
    if (busy) return;
    if (result) onDone(user);
    else onClose();
  }

  if (!user) return null;

  const freeText = (result || preview)?.freeText || [];
  const freeTextList = freeText.length > 0 && (
    <ul style="margin:6px 0 0;padding-left:20px;list-style:disc">
      {freeText.map(f => <li key={f.label}>{f.label}: {f.count}×</li>)}
    </ul>
  );

  return (
    <Modal isOpen title="Gegevens wissen" onClose={close}>
      {error && <div class="alert alert-error mb-3">{error}</div>}

      {result ? (
        <div style="font-size:14px">
          <div class="alert alert-success mb-3">
            De gegevens van {user.display_name} zijn gewist.{' '}
            {result.accountDeleted ? 'Het account bestaat niet meer.' : 'Diegene is ook lid van een andere club; daar blijft het account bestaan.'}
          </div>
          {freeTextList && (
            <div class="mb-3">
              De naam staat nog in tekst die de club zelf schreef. Pas die zo nodig zelf aan:
              {freeTextList}
            </div>
          )}
          <div class="modal-footer">
            <Button onClick={close}>Sluiten</Button>
          </div>
        </div>
      ) : preview && (
        <div style="font-size:14px">
          <p style="margin:0 0 10px">Wis alle gegevens van <strong>{preview.displayName}</strong> in deze club, bijvoorbeeld omdat diegene daarom vraagt. Dit kan niet ongedaan worden gemaakt.</p>
          <ul style="margin:0 0 10px;padding-left:20px;list-style:disc">
            {preview.isActiveMember && <li>Diegene is nu nog lid en verdwijnt uit de ledenlijst.</li>}
            <li>Weg: rollen, kwalificaties en VOG's (met bestanden), certificaten, coins, meldingen, agendakoppelingen en aanmeldingen.</li>
            <li>Blijft, als "Verwijderd lid": afgeronde taken, besluiten, documenten, nieuws en de auditlog (zonder IP-adressen).</li>
            <li>{preview.accountStays ? 'Diegene is ook lid van een andere club; het account blijft daar bestaan.' : 'Het account wordt verwijderd.'}</li>
            <li>Gebruikt de club Sportlink? Verwijder diegene daar ook, anders komen de gegevens bij de volgende koppeling terug.</li>
          </ul>
          {freeTextList && (
            <div class="mb-3" style="padding:10px 12px;border-radius:6px;background:var(--color-warning-bg,#fef3c7);border:1px solid var(--color-warning,#d97706)">
              De naam staat ook in tekst die de club zelf schreef. Die wordt niet aangepast:
              {freeTextList}
            </div>
          )}
          <div class="form-group">
            <label class="form-label">Typ de naam ter bevestiging</label>
            <input class="form-input" type="text" value={typed} placeholder={preview.displayName} autocomplete="off"
              onInput={e => setTyped(e.target.value)} />
          </div>
          <div class="modal-footer">
            <Button variant="secondary" onClick={close} disabled={busy}>Annuleren</Button>
            <Button variant="danger" onClick={erase} disabled={busy || !matches}>{busy ? 'Bezig…' : 'Definitief wissen'}</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
